import { Box, Typography } from '@mui/material';
import { bandsOf, type SchemeLevel } from '../../../components/flashlightGrid';
import type { DomainChart } from './assignmentMonths';
import { ROSTER } from '../tokens';

export const MONTH_WIDTH = 100;
const PAD = 16;
const HEIGHT = 120;
const BASELINE = 104;
const STRIP_WIDTH = 6;
const PRIMARY = '#2A2A8B';
const PRIMARY_LIGHT = '#8181D9';

export function chartX(x: number): number {
  return Math.round((PAD + x * MONTH_WIDTH) * 10) / 10;
}

export function chartY(level: number, top = 5): number {
  return Math.round((BASELINE - ((level - 1) * 96) / Math.max(top - 1, 1)) * 10) / 10;
}

const lineSx = { fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.muted } as const;

export function AssignmentMonthChart({ chart, months, scheme }: { chart: DomainChart; months: string[]; scheme: SchemeLevel[] }) {
  const width = PAD * 2 + months.length * MONTH_WIDTH;
  const bands = bandsOf(scheme);
  const top = bands[bands.length - 1]?.max ?? 5;
  const y = (level: number) => chartY(level, top);
  const cuts = [bands[0]?.min ?? 1, ...bands.slice(0, -1).map((band) => Math.round((band.max + 0.05) * 100) / 100), top];
  return (
    <Box data-testid="assignment-chart" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Box data-testid="asg-head" sx={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <Typography component="span" sx={{ ...lineSx, fontWeight: 600, color: ROSTER.ink }}>{chart.label}</Typography>
        <Typography component="span" sx={lineSx}>{chart.line}</Typography>
      </Box>
      <svg data-testid="asg-svg" width="100%" viewBox={`0 0 ${width} ${HEIGHT}`} preserveAspectRatio="xMinYMid meet" aria-hidden="true" style={{ display: 'block', maxWidth: width }}>
        {bands.map((band, i) => (
          <rect key={band.label} data-mark="level" x={0} y={y(cuts[i + 1]!)} width={STRIP_WIDTH} height={Math.round((y(cuts[i]!) - y(cuts[i + 1]!)) * 10) / 10} fill={band.color} />
        ))}
        {cuts.slice(1, -1).map((cut) => (
          <line key={cut} data-mark="cut" x1={STRIP_WIDTH} y1={y(cut)} x2={width} y2={y(cut)} stroke={ROSTER.border} strokeDasharray="2 3" />
        ))}
        <line x1={0} y1={BASELINE} x2={width} y2={BASELINE} stroke={ROSTER.border} />
        {chart.benchmark && (
          <rect
            data-mark="benchmark"
            x={chartX(chart.benchmark.x) - 6}
            y={y(chart.benchmark.value) - 6}
            width={12}
            height={12}
            transform={`rotate(45 ${chartX(chart.benchmark.x)} ${y(chart.benchmark.value)})`}
            fill={PRIMARY}
          />
        )}
        {chart.points.map((point) => (
          <circle key={point.assignmentId} data-mark="assignment" cx={chartX(point.x)} cy={y(point.average)} r={5} fill={PRIMARY_LIGHT}>
            <title>{`${point.name} · ${point.average.toFixed(1)}`}</title>
          </circle>
        ))}
      </svg>
      <Box data-testid="asg-months" sx={{ display: 'flex', pl: `${PAD}px`, ...lineSx }}>
        {months.map((month) => (
          <Typography key={month} component="span" sx={{ ...lineSx, width: MONTH_WIDTH, textAlign: 'center' }}>{month}</Typography>
        ))}
      </Box>
    </Box>
  );
}
